import { Link } from "react-router-dom";
import { Check, ArrowRight, Minus } from "lucide-react";
import Seo from "@/components/Seo";
import CtaBand from "@/components/CtaBand";
import { Reveal, Eyebrow } from "@/components/Reveal";
import CarePlanModal from "@/components/CarePlanModal";
import Faq from "@/components/Faq";
import { ADDONS } from "@/data/addons";

const tiers = [
  {
    name: "The Starter Workshop",
    tagline: "For someone who just needs to get online",
    price: "$349",
    features: ["1-page website", "Mobile-friendly design", "Business information & services", "Contact section & social links", "Google Maps", "Domain connection"],
    featured: false,
  },
  {
    name: "The Main Street Standard",
    tagline: "Right for most small businesses",
    price: "$599",
    features: ["Up to 4 pages — Home, About, Services, Contact", "Mobile optimization", "Contact form", "Google Maps & social media links", "Basic SEO setup"],
    featured: true,
  },
  {
    name: "Business Plus",
    tagline: "For businesses wanting a more complete presence",
    price: "$849",
    features: ["Up to 6 pages with enhanced layout", "Multiple service sections", "Testimonials", "Lead & contact forms", "Basic local SEO", "Google Analytics", "Google Business Profile connection"],
    featured: false,
  },
  {
    name: "Custom Website",
    tagline: "For larger or more complex projects",
    price: "From $1,000",
    features: ["Custom pages", "Booking systems", "Integrations", "Special features built for you"],
    featured: false,
  },
];

const COMPARISON = [
  { feature: "Pages included", values: ["1 page", "Up to 4", "Up to 6", "Custom"] },
  { feature: "Mobile-friendly design", values: [true, true, true, true] },
  { feature: "Business info, services & contact section", values: [true, true, true, true] },
  { feature: "Social links & Google Maps", values: [true, true, true, true] },
  { feature: "Domain connection", values: [true, true, true, true] },
  { feature: "Contact & lead forms", values: [false, true, true, true] },
  { feature: "Basic SEO setup", values: [false, true, "Local", true] },
  { feature: "Enhanced layout & multiple service sections", values: [false, false, true, true] },
  { feature: "Testimonials", values: [false, false, true, true] },
  { feature: "Google Business Profile connection", values: [false, false, true, true] },
  { feature: "Booking systems", values: [false, false, false, true] },
  { feature: "Special features built for you", values: [false, false, false, true] },
];

const includes = [
  "Mobile-first design that looks right on any phone",
  "Fast loading on rural cell service & cottage wifi",
  "On-page SEO so locals can actually find you",
  "Contact & quote forms that reach your inbox",
  "Help with Google Business Profile & Maps",
  "A walkthrough so you can update things yourself",
];

const steps = [
  { n: "01", title: "We talk", copy: "A relaxed chat about your business, your customers, and what the site needs to do. Coffee optional but encouraged." },
  { n: "02", title: "We build", copy: "You get a clear price and timeline up front, then we get to work. You'll see progress along the way." },
  { n: "03", title: "You review", copy: "We walk through the site together and fine-tune the details until it feels right." },
  { n: "04", title: "We launch", copy: "Your site goes live, your Google listing gets sorted, and customers can start finding you." },
];

const Cell = ({ value }) => {
  if (value === true) return <Check size={18} className="text-[#C58A47] mx-auto" aria-label="Included" />;
  if (value === false) return <Minus size={16} className="text-[#77786F]/40 mx-auto" aria-label="Not included" />;
  return <span className="text-sm font-semibold text-[#314F64]">{value}</span>;
};

const Services = () => (
  <div data-testid="services-page">
    <Seo
      title="Services & Pricing | Good Work Web Co."
      description="Straightforward website packages and honest prices for tradespeople and small businesses in the Almaguin Highlands — from $349 starter sites to full custom builds."
    />

    <section className="py-12 sm:py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Services & Pricing</Eyebrow>
          <h1 className="font-head font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#233D35] mt-3 leading-[0.95] max-w-3xl" data-testid="services-heading">
            Good work, priced plain.
          </h1>
          <p className="mt-5 max-w-2xl text-[#77786F] text-base sm:text-lg leading-relaxed">
            Four honest packages. No confusing tiers, no surprise add-ons, no pushy upsells. Pick the one that fits — or tell us what you need and we'll figure it out together.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 items-stretch">
          {tiers.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.1} className="h-full">
              <div
                data-testid={`pricing-tier-${i}`}
                className={`h-full flex flex-col p-6 sm:p-8 border transition-transform duration-300 hover:-translate-y-1.5 ${
                  t.featured
                    ? "bg-[#233D35] text-[#F3EFE5] border-[#233D35] shadow-[0_10px_40px_rgba(35,61,53,0.25)]"
                    : "bg-white border-[#E2DACB] shadow-[0_4px_20px_rgba(35,61,53,0.06)]"
                }`}
              >
                {t.featured && (
                  <span className="self-start bg-[#C58A47] text-[#F3EFE5] font-head font-bold uppercase tracking-[0.18em] text-[11px] px-3 py-1.5" data-testid="featured-badge">
                    Most Popular
                  </span>
                )}
                <h2 className={`font-head font-bold uppercase tracking-tight text-2xl mt-4 ${t.featured ? "text-[#F3EFE5]" : "text-[#233D35]"}`}>
                  {t.name}
                </h2>
                <p className={`mt-1 text-sm ${t.featured ? "text-[#F3EFE5]/70" : "text-[#77786F]"}`}>{t.tagline}</p>
                <p className="mt-6 font-head font-bold text-4xl sm:text-5xl text-[#C58A47]">{t.price}</p>
                <ul className="mt-6 space-y-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className={`flex items-start gap-3 text-sm ${t.featured ? "text-[#F3EFE5]/85" : "text-[#77786F]"}`}>
                      <Check size={17} className="text-[#C58A47] shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  data-testid={`tier-quote-btn-${i}`}
                  className={`group mt-8 inline-flex items-center justify-center gap-2 font-head font-bold uppercase tracking-[0.12em] px-5 py-3.5 transition-colors duration-200 ${
                    t.featured
                      ? "bg-[#C58A47] hover:bg-[#B0793A] text-[#F3EFE5]"
                      : "border border-[#233D35] text-[#233D35] hover:bg-[#233D35] hover:text-[#F3EFE5]"
                  }`}
                >
                  Start with this
                  <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-8">
          <p className="text-sm text-[#77786F] max-w-2xl" data-testid="pricing-note">
            Prices are honest ballpark starting points — your final quote depends on exactly what you need, and we'll always agree on it before any work begins.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="pb-12 sm:pb-16" data-testid="comparison-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Compare Packages</Eyebrow>
          <h2 className="font-head font-bold uppercase tracking-tight text-3xl sm:text-4xl text-[#233D35] mt-3 leading-[0.95]">
            Side by side, apples to apples.
          </h2>
        </Reveal>
        <Reveal className="mt-10">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <table className="w-full min-w-[760px] border-collapse bg-white border border-[#E2DACB] shadow-[0_4px_20px_rgba(35,61,53,0.06)]" data-testid="comparison-table">
              <thead>
                <tr className="bg-[#233D35] text-[#F3EFE5]">
                  <th scope="col" className="text-left font-head font-bold uppercase tracking-[0.12em] text-sm p-4 sm:p-5">What you get</th>
                  {tiers.map((t) => (
                    <th key={t.name} scope="col" className="text-center font-head font-bold uppercase tracking-[0.1em] text-sm p-4 sm:p-5 border-l border-[#F3EFE5]/10">
                      <span className="block">{t.name}</span>
                      <span className="block mt-1 text-[#C58A47] text-base">{t.price}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, ri) => (
                  <tr key={row.feature} className={ri % 2 === 0 ? "bg-white" : "bg-[#F3EFE5]/60"} data-testid={`compare-row-${ri}`}>
                    <th scope="row" className="text-left text-sm font-medium text-[#233D35] p-4 sm:p-5">{row.feature}</th>
                    {row.values.map((v, vi) => (
                      <td key={vi} className="text-center p-4 sm:p-5 border-l border-[#E2DACB]">
                        <Cell value={v} />
                      </td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="p-4 sm:p-5" />
                  {tiers.map((t, i) => (
                    <td key={t.name} className="p-4 sm:p-5 border-l border-[#E2DACB] text-center">
                      <Link to="/contact" data-testid={`compare-quote-btn-${i}`}
                        className="inline-flex items-center gap-1.5 bg-[#C58A47] hover:bg-[#B0793A] text-[#F3EFE5] font-head font-bold uppercase tracking-[0.1em] text-xs px-4 py-2.5 transition-colors duration-200">
                        Get a Quote
                      </Link>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="bg-[#EBE5D8] py-12 sm:py-16" data-testid="addons-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Add-Ons</Eyebrow>
          <h2 className="font-head font-bold uppercase tracking-tight text-3xl sm:text-4xl text-[#233D35] mt-3 leading-[0.95]">
            Extras, priced plain too.
          </h2>
          <p className="mt-4 max-w-2xl text-[#77786F] text-base leading-relaxed">
            Add any of these to any package — same honest pricing, agreed on before we start.
          </p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ADDONS.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.05}>
              <div className="bg-white border border-[#E2DACB] p-5 flex items-center justify-between gap-3 h-full" data-testid={`addon-${a.id}`}>
                <span className="text-sm font-medium text-[#233D35] leading-snug">{a.label}</span>
                <span className="font-head font-bold text-lg text-[#C58A47] shrink-0">+${a.cost}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8">
          <Link to="/contact" data-testid="addons-quote-link"
            className="group inline-flex items-center gap-2 border border-[#233D35] text-[#233D35] hover:bg-[#233D35] hover:text-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] text-sm px-6 py-3.5 transition-colors duration-200">
            Build my estimate
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>

    <section className="py-12 sm:py-16" data-testid="care-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Ongoing Costs & Support</Eyebrow>
          <h2 className="font-head font-bold uppercase tracking-tight text-3xl sm:text-4xl text-[#233D35] mt-3 leading-[0.95]">
            After launch, no surprises.
          </h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          <Reveal className="lg:col-span-2">
            <div className="border-t-2 border-[#233D35] pt-6" data-testid="ongoing-costs">
              <h3 className="font-head font-bold uppercase tracking-tight text-xl text-[#314F64]">What's separate from your project fee</h3>
              <p className="mt-4 text-sm sm:text-base text-[#77786F] leading-relaxed">
                Your domain, website platform or hosting subscription, paid apps, business email, and any third-party transaction fees are separate from my project fee. You keep ownership of your business accounts and subscriptions; I can help connect them. Actual costs depend on the tools you choose and will be reviewed before purchase.
              </p>
            </div>
          </Reveal>
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              {
                name: "Website Care",
                price: "$25/mo",
                copy: "Basic technical check-ins and minor troubleshooting, with small content edits quoted separately.",
              },
              {
                name: "Website Care Plus",
                price: "$50/mo",
                copy: "Basic care plus up to 30 minutes of small text or image updates per month. Unused time does not carry forward. Larger requests are quoted separately.",
              },
            ].map((p, i) => (
              <Reveal key={p.name} delay={0.1 + i * 0.1} className="h-full">
                <div className="bg-white border border-[#E2DACB] p-6 h-full shadow-[0_4px_20px_rgba(35,61,53,0.06)]" data-testid={`care-plan-${i}`}>
                  <span className="font-head font-semibold uppercase tracking-[0.16em] text-[11px] text-[#C58A47]">Optional</span>
                  <h3 className="font-head font-bold uppercase tracking-tight text-xl text-[#233D35] mt-2">{p.name}</h3>
                  <p className="font-head font-bold text-3xl text-[#C58A47] mt-2">CAD {p.price}</p>
                  <p className="mt-3 text-sm text-[#77786F] leading-relaxed">{p.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal className="mt-8">
          <p className="text-sm text-[#77786F] max-w-2xl" data-testid="care-note">
            Care plans are optional and begin only if agreed after launch. Platform compatibility and access requirements will be confirmed for your specific site.
          </p>
          <div className="mt-5">
            <CarePlanModal />
          </div>
        </Reveal>
      </div>
    </section>

    <section className="py-12 sm:py-16" data-testid="includes-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Every Site Includes</Eyebrow>
          <h2 className="font-head font-bold uppercase tracking-tight text-3xl sm:text-4xl text-[#233D35] mt-3 leading-[0.95]">
            The essentials, done right.
          </h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {includes.map((item, i) => (
            <Reveal key={item} delay={i * 0.06}>
              <div className="bg-white border border-[#E2DACB] p-5 flex items-start gap-3" data-testid={`include-${i}`}>
                <Check size={18} className="text-[#C58A47] shrink-0 mt-0.5" />
                <p className="text-sm text-[#233D35] leading-relaxed">{item}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-[#EBE5D8] py-12 sm:py-16" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>How It Works</Eyebrow>
          <h2 className="font-head font-bold uppercase tracking-tight text-3xl sm:text-4xl text-[#233D35] mt-3 leading-[0.95]">
            Simple from start to finish.
          </h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="border-t-2 border-[#233D35] pt-5" data-testid={`step-${i}`}>
                <span className="font-head font-bold text-[#C58A47] text-lg">{s.n}</span>
                <h3 className="font-head font-bold uppercase tracking-tight text-xl text-[#314F64] mt-2">{s.title}</h3>
                <p className="mt-2 text-sm text-[#77786F] leading-relaxed">{s.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <Faq />

    <CtaBand title="Know what you need? Let's talk numbers." />
  </div>
);

export default Services;
